import React, { useEffect, useMemo, useState } from "react";
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import BusinessIcon from '@mui/icons-material/Business';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ClearIcon from '@mui/icons-material/Clear';
import SearchIcon from '@mui/icons-material/Search';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import EditIcon from '@mui/icons-material/Edit';
import CircularProgress from '@mui/material/CircularProgress';
import { FieldInput } from '../../../ui/FieldInput';
import { AppPagination } from '../../../ui/AppPagination';

type ApiOrganizationRead = {
  id: number;
  full_name: string;
  short_name: string | null;
  legal_address: string | null;
  phone: string | null;
  email: string | null;
  created_at: string;
  updated_at: string;
};

type ApiOrganizationListResponse = {
  total: number;
  page: number;
  size: number;
  pages: number;
  items: ApiOrganizationRead[];
};

export type Organization = {
  id: string;
  name: string;
  fullName: string;
  shortName?: string;
  address?: string;
  email?: string;
  phone?: string;
  createdAt?: string;
};

type OrganizationForm = {
  fullName: string;
  shortName: string;
  address: string;
  email: string;
  phone: string;
};

type Mode = "list" | "view" | "create" | "edit";

type Props = {
  onLoad?: (organizations: Organization[]) => void;
  onSelect?: (organization: Organization | null) => void;
  initialSelectedId?: string | null;
  onOrganizationCreated?: (organization: Organization) => void;
};

const PAGE_SIZE = 8;

const formatDate = (iso: string) =>
  new Date(iso).toLocaleDateString("ru-RU", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

const ORGANIZATIONS_URL = `${process.env.REACT_APP_API_BASE_URL}/api/v1/organizations/`;

const defaultForm: OrganizationForm = {
  fullName: "",
  shortName: "",
  address: "",
  email: "",
  phone: "",
};

const mapApiToUi = (o: ApiOrganizationRead): Organization => ({
  id: String(o.id),
  name: o.short_name || o.full_name,
  fullName: o.full_name,
  shortName: o.short_name ?? undefined,
  address: o.legal_address ?? undefined,
  email: o.email ?? undefined,
  phone: o.phone ?? undefined,
  createdAt: o.created_at,
});

const emptyToNull = (v: string) => (v?.trim() ? v.trim() : null);

const OrganizationListItem: React.FC<{
  org: Organization;
  onClick: () => void;
  isSelected: boolean;
}> = ({ org, onClick, isSelected }) => (
  <button
    type="button"
    onClick={onClick}
    className={`w-full text-left rounded-lg border p-4 shadow-sm transition-all focus:outline-none ${
      isSelected
        ? "bg-blue-50 border-blue-400 shadow-md"
        : "bg-white border-gray-200 hover:border-blue-400 hover:shadow-md"
    }`}
  >
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-3">
        <BusinessIcon className={isSelected ? "text-blue-600" : "text-gray-400"} />
        <div className="flex flex-col">
          <span className={`font-medium ${isSelected ? "text-blue-800" : "text-gray-800"}`}>
            {org.name}
          </span>
          {org.address && (
            <span className="text-xs text-gray-500 truncate max-w-[200px]">
              {org.address}
            </span>
          )}
        </div>
      </div>
      <ChevronRightIcon className={isSelected ? "text-blue-600" : "text-gray-400"} />
    </div>
  </button>
);

export const OrganizationsManager: React.FC<Props> = ({
  onLoad,
  onSelect,
  initialSelectedId,
  onOrganizationCreated,
}) => {
  const [mode, setMode] = useState<Mode>("list");

  // search - то что вбито в инпут, appliedSearch - то по чему реально ищем
  const [search, setSearch] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");

  const [allOrgs, setAllOrgs] = useState<Organization[]>([]);
  const [pageOrgs, setPageOrgs] = useState<Organization[]>([]);
  const [preloadedSelected, setPreloadedSelected] = useState<Organization | null>(null);
  const [selected, setSelected] = useState<Organization | null>(null);

  const [formData, setFormData] = useState<OrganizationForm>(defaultForm);
  const [errors, setErrors] = useState<Partial<Record<keyof OrganizationForm, string>>>({});
  const [flash, setFlash] = useState<string>("");

  const [page, setPage] = useState<number>(1);
  const [totalItems, setTotalItems] = useState<number>(0);
  const [totalPages, setTotalPages] = useState<number>(1);

  const [loading, setLoading] = useState<boolean>(false);
  const [preloading, setPreloading] = useState<boolean>(false);
  const [loadError, setLoadError] = useState<string>("");

  const executeSearch = () => {
    setAppliedSearch(search);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      executeSearch();
    }
  };

  const handleClearSearch = () => {
    setSearch("");
    setAppliedSearch("");
  };

  useEffect(() => {
    if (!initialSelectedId) return;

    const fetchSelected = async () => {
      const token = localStorage.getItem("token");
      if (!token) return;

      setPreloading(true);
      try {
        const res = await fetch(`${ORGANIZATIONS_URL}${initialSelectedId}`, {
          headers: {
            Accept: "application/json",
            Authorization: token,
          },
        });

        if (!res.ok) return;

        const data: ApiOrganizationRead = await res.json();
        const org = mapApiToUi(data);

        setPreloadedSelected(org);
        setSelected(org);
        onSelect?.(org);

        setAllOrgs((prev) => {
          const map = new Map(prev.map((o) => [o.id, o]));
          map.set(org.id, org);
          return Array.from(map.values());
        });
      } catch (e) {
        console.error("Ошибка предзагрузки организации:", e);
      } finally {
        setPreloading(false);
      }
    };

    fetchSelected();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialSelectedId]);

  const fetchPage = async (targetPage: number, searchQuery: string) => {
    const token = localStorage.getItem("token");
    if (!token) return;

    setLoading(true);
    setLoadError("");

    try {
      const q = searchQuery.trim();
      const params = new URLSearchParams({
        page: String(targetPage),
        size: String(PAGE_SIZE),
      });

      let url = `${ORGANIZATIONS_URL}?${params}`;
      if (q) {
        params.append("q", q);
        url = `${ORGANIZATIONS_URL}search?${params}`;
      }

      const res = await fetch(url, {
        headers: {
          Accept: "application/json",
          Authorization: token,
        },
      });

      if (!res.ok) {
        throw new Error(`Ошибка загрузки: ${res.status} ${res.statusText}`);
      }

      const data: ApiOrganizationListResponse = await res.json();
      const mapped = (data.items ?? []).map(mapApiToUi);

      setPageOrgs(mapped);
      setTotalItems(data.total);
      setTotalPages(data.pages);

      setAllOrgs((prev) => {
        const map = new Map(prev.map((o) => [o.id, o]));
        mapped.forEach((o) => map.set(o.id, o));
        return Array.from(map.values());
      });

      onLoad?.(mapped);
    } catch (e: any) {
      console.error(e);
      setLoadError(e?.message || "Не удалось загрузить организации");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPage(page, appliedSearch);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  useEffect(() => {
    if (page !== 1) {
      setPage(1);
    } else {
      fetchPage(1, appliedSearch);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appliedSearch]);

  const displayedOrgs = useMemo(() => {
    if (appliedSearch.trim()) {
      return pageOrgs;
    }

    if (!preloadedSelected) {
      return pageOrgs;
    }

    const withoutSelected = pageOrgs.filter((o) => o.id !== preloadedSelected.id);
    return [preloadedSelected, ...withoutSelected];
  }, [appliedSearch, pageOrgs, preloadedSelected]);

  const openView = (org: Organization) => {
    setSelected(org);
    setMode("view");
    setErrors({});
    onSelect?.(org);
  };

  const openCreate = () => {
    setSelected(null);
    setFormData(defaultForm);
    setErrors({});
    setMode("create");
    onSelect?.(null);
  };

  const openEdit = () => {
    if (!selected) return;
    setFormData({
      fullName: selected.fullName,
      shortName: selected.shortName || "",
      address: selected.address || "",
      email: selected.email || "",
      phone: selected.phone || "",
    });
    setErrors({});
    setMode("edit");
  };

  const closePanel = () => {
    setMode("list");
  };

  const validate = (v: OrganizationForm) => {
    const e: Partial<Record<keyof OrganizationForm, string>> = {};
    if (!v.fullName.trim()) e.fullName = "Укажите полное название";
    if (v.email && !/\S+@\S+\.\S+/.test(v.email)) e.email = "Некорректный email";
    return e;
  };

  const handleFormChange = (field: keyof OrganizationForm, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  const onSave = async (e: React.FormEvent) => {
    e.preventDefault();
    const eMap = validate(formData);
    setErrors(eMap);
    if (Object.keys(eMap).length > 0) return;

    const token = localStorage.getItem("token");
    const isEdit = mode === "edit";

    const body = {
      full_name: formData.fullName.trim(),
      short_name: emptyToNull(formData.shortName),
      legal_address: emptyToNull(formData.address),
      phone: emptyToNull(formData.phone),
      email: emptyToNull(formData.email),
    };

    const url = isEdit ? `${ORGANIZATIONS_URL}${selected!.id}` : ORGANIZATIONS_URL;
    const method = isEdit ? "PUT" : "POST";

    try {
      const res = await fetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          Authorization: token || "",
        },
        body: JSON.stringify(body),
      });

      if (!res.ok) {
        let message = `Ошибка ${isEdit ? "сохранения" : "создания"} (${res.status})`;
        try {
          const errBody = await res.json();
          message = errBody?.detail || JSON.stringify(errBody) || message;
        } catch {}
        throw new Error(message);
      }

      const savedData: ApiOrganizationRead = await res.json();
      const savedOrg = mapApiToUi(savedData);

      if (isEdit) {
        setAllOrgs((prev) => prev.map((o) => (o.id === savedOrg.id ? savedOrg : o)));
        setPageOrgs((prev) => prev.map((o) => (o.id === savedOrg.id ? savedOrg : o)));
        if (preloadedSelected?.id === savedOrg.id) {
          setPreloadedSelected(savedOrg);
        }
        setSelected(savedOrg);
        setFlash("Организация успешно обновлена");
        setMode("view");
      } else {
        setAllOrgs((prev) => [savedOrg, ...prev]);
        onOrganizationCreated?.(savedOrg);
        setPage(1);
        await fetchPage(1, appliedSearch);
        setFlash("Организация успешно создана");
        openView(savedOrg);
      }

      setTimeout(() => setFlash(""), 3000);
    } catch (err: any) {
      setFlash(err?.message || "Не удалось сохранить организацию");
      setTimeout(() => setFlash(""), 3000);
    }
  };

  const isPanelOpen = mode === "view" || mode === "create" || mode === "edit";
  const isLoading = loading || preloading;

  return (
    <section className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden w-full">
      <header className="flex items-center justify-between gap-4 p-4 sm:p-5 border-b border-gray-200">
        <div className="flex items-center gap-3">
          <BusinessIcon className="text-gray-500" />
          <h1 className="text-lg font-medium text-gray-900">Организации</h1>
        </div>
        <button
          type="button"
          onClick={mode === "create" ? closePanel : openCreate}
          className={`inline-flex items-center justify-center gap-2 rounded-md px-4 py-2 text-sm font-semibold text-white shadow-sm transition-colors focus:outline-none ${
            mode === "create"
              ? "bg-gray-600 hover:bg-gray-700"
              : "bg-blue-600 hover:bg-blue-700"
          }`}
        >
          {mode === "create" ? (
            <ArrowBackIcon fontSize="small" />
          ) : (
            <AddIcon fontSize="small" />
          )}
          {mode === "create" ? "К списку" : "Создать организацию"}
        </button>
      </header>

      <div className="p-4 sm:p-6">
        {flash && (
          <div
            className={`mb-4 flex items-center gap-3 text-sm rounded-lg px-4 py-3 border ${
              flash.includes("Ошибка") || flash.includes("Не удалось")
                ? "bg-red-50 text-red-800 border-red-200"
                : "bg-green-100 text-green-800 border-green-200"
            }`}
          >
            <CheckCircleOutlineIcon
              className={flash.includes("Ошибка") ? "text-red-600" : "text-green-600"}
            />
            {flash}
          </div>
        )}

        <div
          className={`grid grid-cols-1 gap-8 transition-all duration-300 ease-in-out ${
            mode === "view" || mode === "edit" ? "lg:grid-cols-2" : "lg:grid-cols-1"
          }`}
        >
          {/* Список */}
          <div className={`space-y-4 ${mode === "create" ? "hidden" : ""}`}>
            {/* Панель поиска */}
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="relative flex-1">
                <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                  <SearchIcon className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Поиск организации..."
                  className="w-full rounded-md border-gray-300 bg-white py-2 pl-10 pr-10 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                />
                {search && (
                  <button
                    type="button"
                    onClick={handleClearSearch}
                    className="absolute inset-y-0 right-0 flex items-center pr-3"
                  >
                    <ClearIcon className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                  </button>
                )}
              </div>
              <button
                type="button"
                onClick={executeSearch}
                disabled={isLoading}
                className="flex items-center justify-center px-6 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 shadow-sm transition disabled:opacity-50"
              >
                <SearchIcon fontSize="small" className="mr-2" />
                Найти
              </button>
            </div>

            <div className="space-y-3">
              {isLoading ? (
                <div className="flex justify-center p-12">
                  <CircularProgress />
                </div>
              ) : loadError ? (
                <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-red-700">
                  {loadError}
                </div>
              ) : displayedOrgs.length === 0 ? (
                <div className="rounded-lg border-2 border-dashed border-gray-300 p-12 text-center">
                  <h3 className="text-lg font-medium text-gray-500">
                    {appliedSearch ? "Ничего не найдено" : "Организации отсутствуют"}
                  </h3>
                  <p className="mt-1 text-sm text-gray-500">
                    {appliedSearch
                      ? "Попробуйте изменить запрос."
                      : "Нажмите 'Создать', чтобы добавить первую."}
                  </p>
                </div>
              ) : (
                <>
                  {displayedOrgs.map((org) => (
                    <OrganizationListItem
                      key={org.id}
                      org={org}
                      onClick={() => openView(org)}
                      isSelected={!!selected && selected.id === org.id}
                    />
                  ))}
                  {totalPages > 1 && (
                    <AppPagination
                      page={page}
                      totalItems={totalItems}
                      pageSize={PAGE_SIZE}
                      onChange={setPage}
                      className="pt-2"
                    />
                  )}
                </>
              )}
            </div>
          </div>

          {/* Панель просмотра / создания / редактирования */}
          {isPanelOpen && (
            <div
              className={
                mode === "view" || mode === "edit" ? "border-l border-gray-100 pl-8" : ""
              }
            >
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={closePanel}
                    className="inline-flex items-center justify-center p-1.5 rounded-md text-gray-500 hover:bg-gray-100 focus:outline-none"
                  >
                    <ArrowBackIcon />
                  </button>
                  <h2 className="text-xl font-semibold text-gray-900">
                    {mode === "view"
                      ? "Информация об организации"
                      : mode === "edit"
                      ? "Редактирование организации"
                      : "Новая организация"}
                  </h2>
                </div>
              </div>

              <form onSubmit={onSave} noValidate>
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-5">
                    <FieldInput
                      className="md:col-span-2"
                      label="Полное название"
                      value={mode === "view" ? selected!.fullName : formData.fullName}
                      onChange={(v) => handleFormChange("fullName", v)}
                      error={errors.fullName}
                      required
                      disabled={mode === "view"}
                    />
                    <FieldInput
                      className="md:col-span-2"
                      label="Краткое название"
                      value={
                        mode === "view" ? (selected!.shortName ?? "") : formData.shortName
                      }
                      onChange={(v) => handleFormChange("shortName", v)}
                      disabled={mode === "view"}
                    />
                    <FieldInput
                      label="Email"
                      type="email"
                      value={mode === "view" ? (selected!.email ?? "") : formData.email}
                      onChange={(v) => handleFormChange("email", v)}
                      error={errors.email}
                      disabled={mode === "view"}
                    />
                    <FieldInput
                      label="Телефон"
                      value={mode === "view" ? (selected!.phone ?? "") : formData.phone}
                      onChange={(v) => handleFormChange("phone", v)}
                      disabled={mode === "view"}
                      placeholder="+7 (___) ___-__-__"
                      mask="+{7} (000) 000-00-00"
                      inputMode="tel"
                    />
                    <FieldInput
                      className="md:col-span-2"
                      label="Юридический адрес"
                      value={mode === "view" ? (selected!.address ?? "") : formData.address}
                      onChange={(v) => handleFormChange("address", v)}
                      disabled={mode === "view"}
                    />
                    {mode === "view" && selected?.createdAt && (
                      <FieldInput
                        className="md:col-span-2"
                        label="Дата создания"
                        value={formatDate(selected.createdAt)}
                        disabled
                      />
                    )}
                  </div>
                </div>

                {mode === "view" && (
                  <div className="mt-8 pt-5 border-t border-gray-100 flex justify-end">
                    <button
                      type="button"
                      onClick={openEdit}
                      className="inline-flex justify-center items-center gap-2 rounded-md bg-white border border-gray-300 px-6 py-2.5 text-sm font-semibold text-gray-700 shadow-sm hover:bg-gray-50 transition-colors"
                    >
                      <EditIcon fontSize="small" />
                      Редактировать
                    </button>
                  </div>
                )}

                {(mode === "create" || mode === "edit") && (
                  <div className="mt-8 pt-5 border-t border-gray-100 flex justify-end gap-3">
                    {mode === "edit" && (
                      <button
                        type="button"
                        onClick={() => openView(selected!)}
                        className="inline-flex justify-center gap-2 rounded-md bg-white px-6 py-2.5 text-sm font-semibold text-gray-700 border border-gray-300 shadow-sm hover:bg-gray-50 transition-colors"
                      >
                        Отмена
                      </button>
                    )}
                    <button
                      type="submit"
                      className="inline-flex justify-center gap-2 rounded-md bg-blue-600 px-6 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-blue-700 transition-colors"
                    >
                      {mode === "create" ? "Создать организацию" : "Сохранить изменения"}
                    </button>
                  </div>
                )}
              </form>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};